import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select'
import { createPopper } from '@popperjs/core'

export default class extends Controller {
  static values = {
    selectedIds: Array,
    dragAndDrop: { type: Boolean, default: false },
    dropdownInput: { type: Boolean, default: false },
    dropdownParent: { type: String, default: '' },
    allowEmptyOption: { type: Boolean, default: false },
    allowCreate: { type: Boolean, default: false },
    usePopper: { type: Boolean, default: false },
    useCustomOptionRender: { type: Boolean, default: false },
    useCustomItemRender: { type: Boolean, default: false },
    useRedirect: { type: Boolean, default: false },
    closeAfterSelect: { type: Boolean, default: true },
    dispatchEvent: { type: Boolean, default: false },
    refreshThrottle: { type: Number, default: 0 },
    lockOptgroupOrder: { type: Boolean, default: false },
    // An accessible name for the visible combobox, for the cases TomSelect cannot name itself.
    //
    // TomSelect names its widget by looking for `label[for="<select id>"]` and nothing else — it
    // never reads `aria-label` off the select. So a select with a visible label that has no `for`,
    // or one of several selects sharing an id, ends up with a widget announcing nothing at all.
    // Pass the label text here in those cases. CU-86cb4v1wb.
    label: { type: String, default: '' }
  }

  connect() {
    // skip TomSelect in pdf views, just regular select will be rendered
    const metaEl = document.querySelector("meta[name='pdf']")
    if(metaEl && metaEl.getAttribute('content') == 'true') {
      return
    }

    const el = this.element
    const htmlEl = document.getElementsByTagName('html')[0]

    let config = {
      maxOptions: null,
      hidePlaceholder: true, // hides placeholder when some options are already selected
      duplicates: true,
      closeAfterSelect: this.closeAfterSelectValue,
      lockOptgroupOrder: this.lockOptgroupOrderValue // maintain the order of optgroups as defined in the optgroups array
    }

    if(this.allowEmptyOptionValue) {
      config.allowEmptyOption = true
    }

    const context = this

    if(this.usePopperValue) {
      const popper_options = {
        modifiers: [
          {
            name: 'flip',
            options: {
              flipVariations: false
            }
          }
        ]
      }

      config.onInitialize = function() {
        // this in context of this function is instance of TomSelect
        this.popper = createPopper(this.control, this.dropdown, popper_options)
      }
    }

    config.onDropdownOpen = function() {
      // `this` here is the TomSelect instance, so read the controller's usePopperValue
      // via the captured `context`. Recompute on open: the popper created in onInitialize
      // ran while the dropdown was display:none (zero-size) and the modal was still settling,
      // so its placement is stale; update() repositions against the now-visible control.
      if(context.usePopperValue && this.popper) {
        this.popper.update()
      }

      // A body-parented dropdown is positioned once, before this callback, while it still sits at its
      // default spot below the viewport. On pages that scroll inside a panel rather than <html>, that
      // briefly adds a page scrollbar, so the first open measures a narrower, shifted control. The
      // dropdown is under the control by now and the scrollbar is gone, so measure again.
      if(this.settings.dropdownParent === 'body') {
        this.positionDropdown()
      }

      htmlEl.setAttribute('data-disable-scroll', 'true')

      // add data attribute to parent table responsive of current TomSelect instance
      let tableResponsiveEl = context.element.closest('.table-responsive')
      if(tableResponsiveEl) {
        tableResponsiveEl.setAttribute('data-has-tom-select-opened', true)
      }
    }

    config.onDropdownClose = function() {
      htmlEl.removeAttribute('data-disable-scroll')

      let tableResponsiveEl = context.element.closest('.table-responsive')
      if(tableResponsiveEl) {
        tableResponsiveEl.removeAttribute('data-has-tom-select-opened')
      }
    }

    // Position Dropdown
    if(this.dropdownParentValue && this.dropdownParentValue != '') {
      // use dropdownParent selector passed with data
      config.dropdownParent = this.dropdownParentValue
    } else if(this.inTableResponsive() && !this.inModal()) {
      // if dropdown rendered in .table-responsive but not in modal use body tag
      config.dropdownParent = 'body'
    }

    if(el.multiple) {
      config.plugins = [
        'dropdown_input',
        'remove_button'
      ]

      if(this.inFilter()) {
        config.plugins.push('input_autogrow')
      }

      // workaround to keep custom order of items, independent of order of available options
      if(this.selectedIdsValue && this.selectedIdsValue.length > 0) {
        config.items = this.selectedIdsValue
      }

      if(!el.getAttribute('placeholder')) {
        config.placeholder = 'Select some options'
      }
    } else { // single select
      config.plugins = ['clear_button']

      if(this.dropdownInputValue) {
        config.plugins.push('dropdown_input')
      }

      if(!el.getAttribute('placeholder')) {
        config.placeholder ='Select an option'
      }

      // redirect page to the url specified as data-tomselect-redirect-url option attribute
      if(this.useRedirectValue) {
        config.onChange = function(value) {
          const originalSelect = this.input
          const selectedOption = originalSelect.querySelector(`option[value="${value}"]`)

          const redirectURL = selectedOption.getAttribute('data-tomselect-redirect-url')
          if(redirectURL) {
            window.location.href = redirectURL
          }
        }
      }
    }

    if(this.allowCreateValue) {
      config.create = true
      config.createOnBlur = true

      config.createFilter = function(input) {
        input = input.toLowerCase()
        return !(input in this.options)
      }
    }

    config.render = {}

    // custom render for options
    if(this.useCustomOptionRenderValue) {
      config.render.option = (data, escape) => {
        return `<div value="${data.value}"` +
            (data.extraCssClass ? ` class="${data.extraCssClass}">` : '>') +
            escape(data.text) +
            '</div>'
      }
    }

    // custom render for selected option
    if(this.useCustomItemRenderValue) {
      config.render.item = function(data, escape) {
        return `<div value="${data.value}"` +
            (data.extraCssClass ? ` class="${data.extraCssClass}">` : '>') +
            escape(data.text) +
            '</div>'
      }
    }

    if(this.dragAndDropValue) {
      config.onInitialize = async () => {
        const Sortable = await this.#sortableConditionalImport()

        const tsControl = this.tomSelect.control
        if(!tsControl) {
          return
        }

        const sortable = Sortable.create(tsControl, {
          handle: '.ts-item-drag-handle',
          animation: 150,
          draggable: '.ts-item-draggable',
          forceFallback: true, // Force HTML5 DnD fallback for better compatibility
          fallbackClass: 'sortable-fallback',
          ghostClass: 'sortable-ghost',
          chosenClass: 'sortable-chosen',
          dragClass: 'sortable-drag',
          onChoose: (event) => {
            // Prevent TomSelect from interfering
            event.item.style.pointerEvents = 'none'
          },
          onEnd: (event) => {
            // Restore pointer events
            event.item.style.pointerEvents = 'auto'
          },
          onUpdate: (event) => {
            // Get the new order from the DOM items
            const items = Array.from(tsControl.querySelectorAll('.item'))
            const newOrder = []

            // Get current values and items from TomSelect
            const currentValues = this.tomSelect.getValue()

            // Map DOM order to values by matching text content
            items.forEach((domItem, index) => {
              const itemText = domItem.textContent.replace('⋮⋮', '').replace('×', '').trim()
              // Find the corresponding value by matching text
              for (let value of currentValues) {
                const option = this.tomSelect.options[value]
                if (option && option.text === itemText) {
                  newOrder.push(value)
                  break
                }
              }
            })

            // Update TomSelect with the new order
            if (newOrder.length > 0 && newOrder.length === currentValues.length) {
              this.tomSelect.setValue(newOrder, true) // true = silent (no events)
            }
          }
        })
      }

      config.render.item = function(data, escape) {
        return `<div class="item ts-item-draggable">
                <span class="ts-item-drag-handle si-arrow-double-h-circle-o"></span>
                ${escape(data.text)}
            </div>`
      }
    }

    if(this.refreshThrottleValue !== undefined && this.refreshThrottleValue !== null && this.refreshThrottleValue !== 0) {
      config.refreshThrottle = this.refreshThrottleValue
    }

    if(this.dispatchEventValue) {
      config.onInitialize = () => {
        const ts_initialized_event = new CustomEvent('tomselect:initialized', { detail: { selector: `#${el.getAttribute('id')}` } })
        document.dispatchEvent(ts_initialized_event)
      }
    }

    if(this.closeAfterSelectValue == false) {
      config.onItemAdd = () => {
        this.tomSelect.setTextboxValue('')
        this.tomSelect.refreshOptions(false)
      }
    }

    this.tomSelect = new TomSelect(el, config)
    // this.repairDropdownInputCombobox()
    this.repairAccessibility(el)
  }

  // TomSelect's `dropdown_input` plugin makes a multi-select impossible to complete without a
  // mouse. CU-1248uf3tdjm, WCAG 2.1.1 Keyboard, level A — heard in the September 2026 listening
  // pass as "Blank, Select some options" and nothing else, however far you arrowed.
  //
  // The plugin sets `focus_node = control` — so `role="combobox"` and, at runtime,
  // `aria-activedescendant` go on the control div — and then moves the real <input> into the
  // dropdown and focuses it when the dropdown opens ("give the control_input focus when the
  // dropdown is open", its own comment). `aria-activedescendant` only means anything on the
  // element that holds DOM focus. So arrowing moved the highlight and told assistive technology
  // nothing at all: the active option was being announced on an element nobody was focused on.
  // What is left to hear is the `items-placeholder` input the plugin injects, which is where the
  // reported "Select some options" came from.
  //
  // The input is the combobox in this pattern — the popup holds the listbox — so the ARIA moves
  // onto it, and `focus_node` with it. That last part matters most: TomSelect writes
  // `aria-expanded` on open and close, and `aria-activedescendant` on every arrow key, to
  // `focus_node`. Reassigning it means every one of those runtime writes lands on the focused
  // element without patching TomSelect itself.
  //
  // Nothing moves on screen. This is which element carries the attributes, not where anything sits.
  repairDropdownInputCombobox() {
    const ts = this.tomSelect
    if (!ts) return

    const control = ts.control
    const input = ts.control_input
    if (!control || !input || control === input) return

    // Test the situation, not the plugin name. `dropdown_input` is what causes this today, but
    // what actually matters is whether the input has been moved inside the dropdown — that is the
    // split between the focused element and the element carrying the ARIA. Checking
    // `plugins.names` instead would mean a TomSelect rename silently stops applying this and hands
    // back a level A failure with nothing to catch it. Widgets where the input still sits in the
    // control are already correct and are left alone.
    if (!ts.dropdown || !ts.dropdown.contains(input)) return

    // Move rather than copy: leaving role="combobox" behind would put two comboboxes in the
    // accessibility tree, which trades one defect for another.
    const carried = ['role', 'aria-haspopup', 'aria-expanded', 'aria-controls',
                     'aria-labelledby', 'aria-label', 'aria-activedescendant']
    carried.forEach((attr) => {
      const value = control.getAttribute(attr)
      if (value === null) return
      input.setAttribute(attr, value)
      control.removeAttribute(attr)
    })

    ts.focus_node = input

    // One tab stop, not two. The plugin restores `focus_node.tabIndex` on blur so the widget stays
    // tabbable, and reassigning focus_node above would therefore put the dropdown's own input into
    // the tab order alongside the control — landing a keyboard user inside a closed dropdown. Keep
    // the control as the single tab stop and the input reachable only programmatically, which is
    // how TomSelect focuses it on open in any case. Registered after the plugin's own blur handler,
    // so this runs last and wins.
    const keepOneTabStop = () => {
      input.tabIndex = -1
      control.tabIndex = ts.isDisabled ? -1 : ts.tabIndex
    }
    keepOneTabStop()
    ts.on('blur', keepOneTabStop)
    ts.on('dropdown_close', keepOneTabStop)
  }

  // TomSelect leaves two accessibility problems behind, on every dropdown, in every product.
  // Neither is our code's doing — both are the library's defaults — so this repairs them after
  // initialisation rather than trying to change what TomSelect does. CU-86cb4v1wb.
  repairAccessibility(el) {
    // 1. The native select stays exposed to assistive technology with no name.
    //
    // TomSelect hides it with `.ts-hidden-accessible`, which clips it rather than removing it, and
    // moves our `<label for>` to its own widget. So the original is left in the accessibility tree,
    // nameless, duplicating every option — 31 of them on the admin new-user form alone. It already
    // carries `tabindex="-1"` from TomSelect, so hiding it cannot orphan focus.
    el.setAttribute('aria-hidden', 'true')

    // 2. The visible widget may have no name either.
    //
    // TomSelect looks for `label[for="<select id>"]` and nothing else. A visible label without a
    // `for`, or several selects sharing an id, leaves the combobox announcing nothing. Only set a
    // name where it is actually missing, so TomSelect's own correct wiring is never overwritten.
    //
    // `focus_node` is TomSelect's own reference to the element it puts `role="combobox"` on, so it
    // is exact. Do not go looking through the DOM for it: the select is a SIBLING of `.ts-wrapper`,
    // not inside it, so a search from the select's parent finds the first combobox in the
    // surrounding card — which on the medic-groups form would name all fifteen widgets after the
    // first one.
    const combobox = this.tomSelect.focus_node
    if (!combobox || combobox.getAttribute('aria-labelledby') || combobox.getAttribute('aria-label')) {
      return
    }

    const fallback = this.labelValue || el.getAttribute('aria-label') || el.getAttribute('title')
    if (fallback) combobox.setAttribute('aria-label', fallback)
  }

  inTableResponsive() {
    return !!this.element.closest('.table-responsive')
  }

  inCardBody() {
    return !!this.element.closest('.card-body')
  }

  inModal() {
    return !!this.element.closest('.modal')
  }

  inFilter() {
    return !!this.element.closest('.filters')
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
  }

  async #sortableConditionalImport() {
    if (typeof Sortable === 'undefined') {
        const { default: Sortable } = await import('sortablejs')
        return Sortable
    }
    return window.Sortable
  }
}
